import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';

// 定义Point接口
interface Point {
  x: number;
  y: number;
}

// 全局FFmpeg实例（单例模式）
let ffmpegInstance: FFmpeg | null = null;
let isFFmpegLoaded = false;

/**
 * 初始化FFmpeg实例
 */
async function initFFmpeg(): Promise<FFmpeg> {
  if (ffmpegInstance && isFFmpegLoaded) {
    return ffmpegInstance;
  }

  ffmpegInstance = new FFmpeg();
  
  // 设置日志回调（可选）
  ffmpegInstance.on('log', ({ message }) => {
    console.log('FFmpeg:', message);
  });
  
  // 设置进度回调（可选）
  ffmpegInstance.on('progress', ({ progress, time }) => {
    console.log(`FFmpeg Progress: ${Math.round(progress * 100)}% (${time}s)`);
  });

  try {
    // 加载FFmpeg核心文件
    const baseURL = '/ffmpeg';
    
    await ffmpegInstance.load({
      coreURL: await toBlobURL(`${baseURL}/core.js`, 'text/javascript'),
      wasmURL: await toBlobURL(`${baseURL}/core.wasm`, 'application/wasm'),
      workerURL: await toBlobURL(`${baseURL}/core.worker.js`, 'text/javascript')
    });
    
    isFFmpegLoaded = true;
    console.log('FFmpeg loaded successfully');
    return ffmpegInstance;
    
  } catch (error) {
    console.error('Failed to load FFmpeg:', error);
    throw new Error('FFmpeg 加载失败，请检查网络连接');
  }
}

/**
 * 计算裁剪区域的边界矩形
 */
function calculateCropArea(corners: [Point, Point, Point, Point]) {
  const [tl, tr, br, bl] = corners;
  const allX = [tl.x, tr.x, br.x, bl.x];
  const allY = [tl.y, tr.y, br.y, bl.y];
  
  const minX = Math.min(...allX);
  const maxX = Math.max(...allX);
  const minY = Math.min(...allY);
  const maxY = Math.max(...allY);
  
  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY
  };
}

/**
 * 判断是否为规则矩形裁剪
 */
function isRectangularCrop(corners: [Point, Point, Point, Point]): boolean {
  const [tl, tr, br, bl] = corners;
  
  // 检查是否形成矩形
  const isRectangle = 
    Math.abs(tl.y - tr.y) < 2 && // 上边水平
    Math.abs(bl.y - br.y) < 2 && // 下边水平
    Math.abs(tl.x - bl.x) < 2 && // 左边垂直
    Math.abs(tr.x - br.x) < 2;   // 右边垂直
    
  return isRectangle;
}

/**
 * 验证裁剪坐标
 */
function validateCropCoordinates(corners: [Point, Point, Point, Point]): void {
  if (!corners || corners.length !== 4) {
    throw new Error('裁剪坐标必须包含4个点');
  }
  
  for (const point of corners) {
    if (typeof point.x !== 'number' || typeof point.y !== 'number') {
      throw new Error('坐标点必须包含有效的x和y数值');
    }
    if (point.x < 0 || point.y < 0) {
      throw new Error('坐标值不能为负数');
    }
  }
}

/**
 * 使用FFmpeg.wasm进行视频裁剪
 * @param file - 输入视频文件
 * @param corners - 四个角坐标 [左上, 右上, 右下, 左下]
 * @returns Promise<File> - 裁剪后的视频文件
 */
export async function cropVideo(
  file: File,
  corners: [Point, Point, Point, Point]
): Promise<File> {
  try {
    // 验证输入
    validateCropCoordinates(corners);
    
    // 初始化FFmpeg
    const ffmpeg = await initFFmpeg();
    
    // 计算裁剪区域
    const cropArea = calculateCropArea(corners);
    const { x, y, width, height } = cropArea;
    
    // 确保尺寸为偶数（视频编码要求）
    const cropX = Math.floor(x / 2) * 2;
    const cropY = Math.floor(y / 2) * 2;
    const cropWidth = Math.floor(width / 2) * 2;
    const cropHeight = Math.floor(height / 2) * 2;
    
    // 生成唯一的文件名
    const timestamp = Date.now();
    const inputName = `input_${timestamp}.mp4`;
    const outputName = `output_${timestamp}.mp4`;
    
    console.log('开始处理视频裁剪...');
    console.log(`裁剪区域: x=${cropX}, y=${cropY}, width=${cropWidth}, height=${cropHeight}`);
    
    // 写入输入文件到FFmpeg文件系统
    await ffmpeg.writeFile(inputName, await fetchFile(file));
    
    if (isRectangularCrop(corners)) {
      // 规则矩形裁剪 - 使用最优化参数
      console.log('使用规则矩形裁剪模式');
      
      console.time('cropVideo');
      console.log("可用线程数量：", navigator.hardwareConcurrency)
      await ffmpeg.exec([
        '-i', inputName,
        '-filter:v', `crop=${cropWidth}:${cropHeight}:${cropX}:${cropY}`,
        '-c:v', 'libx264',
        '-preset', 'ultrafast',
        '-tune', 'fastdecode', // 优化解码速度
        '-crf', '23',
        '-threads', `${Math.min(navigator.hardwareConcurrency, 8)}`,
        '-g', '24', // 关键帧间隔
        '-bf', '0',
        '-r', '24', // 帧率
        '-sc_threshold', '0', // 禁用场景切换检测
        '-c:a', 'copy', // 复制音频
        // '-an', // 禁用音频(提效低 没有必要禁用)
        '-movflags', '+faststart',
        '-avoid_negative_ts', 'make_zero',
        '-fflags', '+genpts', // 生成时间戳
        outputName
      ]);
      console.timeEnd('cropVideo');
    } else {
      // 不规则裁剪优化
      const [tl, tr, br, bl] = corners;
      
      const perspectiveFilter = [
        `perspective=`,
        `x0=${tl.x}:y0=${tl.y}`,
        `x1=${tr.x}:y1=${tr.y}`,
        `x2=${br.x}:y2=${br.y}`,
        `x3=${bl.x}:y3=${bl.y}`,
        `interpolation=linear`
      ].join(':');
      
      console.time('cropVideo');
      await ffmpeg.exec([
        '-i', inputName,
        '-filter:v', perspectiveFilter,
        '-c:v', 'libx264',
        '-preset', 'ultrafast',
        '-tune', 'fastdecode',
        '-crf', '23',
        '-threads', `${Math.min(navigator.hardwareConcurrency, 8)}`,
        '-g', '30',
        '-sc_threshold', '0',
        '-c:a', 'copy',
        '-movflags', '+faststart',
        '-avoid_negative_ts', 'make_zero',
        '-fflags', '+genpts',
        outputName
      ]);
      console.timeEnd('cropVideo');
    }
    
    // 读取输出文件
    const outputData = await ffmpeg.readFile(outputName);
    
    // 清理临时文件
    await ffmpeg.deleteFile(inputName);
    await ffmpeg.deleteFile(outputName);
    
    // 创建输出文件
    const outputBlob = new Blob([outputData], { type: 'video/mp4' });
    const croppedFile = new File([outputBlob], `cropped_${file.name}`, {
      type: 'video/mp4'
    });
    
    console.log('视频裁剪完成！');
    return croppedFile;
    
  } catch (error) {
    console.error('视频裁剪失败:', error);
    throw new Error(`视频裁剪失败: ${error instanceof Error ? error.message : '未知错误'}`);
  }
}

/**
 * 检查FFmpeg.wasm支持情况
 */
export function checkFFmpegSupport(): {
  supported: boolean;
  features: string[];
  missing: string[];
} {
  const features: string[] = [];
  const missing: string[] = [];
  
  // 检查WebAssembly支持
  if (typeof WebAssembly !== 'undefined') {
    features.push('WebAssembly');
  } else {
    missing.push('WebAssembly');
  }
  
  // 检查SharedArrayBuffer支持（多线程需要）
  if (typeof SharedArrayBuffer !== 'undefined') {
    features.push('SharedArrayBuffer');
  } else {
    missing.push('SharedArrayBuffer (多线程支持)');
  }
  
  // 检查Worker支持
  if (typeof Worker !== 'undefined') {
    features.push('Web Workers');
  } else {
    missing.push('Web Workers');
  }
  
  // 检查跨域隔离状态
  if (typeof crossOriginIsolated !== 'undefined' && crossOriginIsolated) {
    features.push('Cross-Origin Isolation');
  } else {
    missing.push('Cross-Origin Isolation (性能优化)');
  }
  
  return {
    supported: missing.length === 0 || (missing.length === 1 && missing[0].includes('Cross-Origin')),
    features,
    missing
  };
}

/**
 * 获取视频信息（可选功能）
 */
export async function getVideoInfo(file: File): Promise<{
  duration: number;
  width: number;
  height: number;
  fps: number;
}> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    
    video.onloadedmetadata = () => {
      resolve({
        duration: video.duration,
        width: video.videoWidth,
        height: video.videoHeight,
        fps: 24 // 默认值，实际FPS需要更复杂的检测
      });
      URL.revokeObjectURL(video.src);
    };
    
    video.onerror = () => {
      reject(new Error('无法读取视频信息'));
      URL.revokeObjectURL(video.src);
    };
    
    video.src = URL.createObjectURL(file);
  });
}

/**
 * 清理FFmpeg资源（可选）
 */
export async function cleanupFFmpeg(): Promise<void> {
  if (ffmpegInstance && isFFmpegLoaded) {
    try {
      await ffmpegInstance.terminate();
      ffmpegInstance = null;
      isFFmpegLoaded = false;
      console.log('FFmpeg resources cleaned up');
    } catch (error) {
      console.warn('Error cleaning up FFmpeg:', error);
    }
  }
}

// 使用示例
/*
// 基本使用
const croppedVideo = await cropVideo(videoFile, [
  { x: 100, y: 100 }, // 左上
  { x: 500, y: 100 }, // 右上
  { x: 500, y: 400 }, // 右下
  { x: 100, y: 400 }  // 左下
]);

// 检查支持情况
const support = checkFFmpegSupport();
if (!support.supported) {
  console.warn('FFmpeg.wasm 不完全支持:', support.missing);
}

// 获取视频信息
const videoInfo = await getVideoInfo(videoFile);
console.log('视频信息:', videoInfo);
*/
